
import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { Helmet, HelmetProvider } from "react-helmet-async"
import { useTranslation } from "react-i18next"
import { useNavigate, useParams } from "react-router-dom"
import api from "../api"
import { ReactComponent as CheckIcon } from "../assets/images/icons/ic-check.svg"
import { ReactComponent as ChevronIcon } from "../assets/images/icons/ic-chevron.svg"
import { ReactComponent as ErrorIcon } from "../assets/images/icons/ic-close.svg"
import UserPlaceholder from "../assets/images/placeholders/user-placeholder.png"
import { Each } from "../common/Each"
import useWindowDimensions from "../common/hooks/useWindowDimensions"
import Button from "../components/Button"
import Card from "../components/cards/Card"
import CircularProgress from "../components/CircularProgress"
import SimpleCollapsable from "../components/SimpleCollapsable"
import Tag from "../components/Tag"
import HeaderFooterLayout from "../components/layouts/HeaderFooterLayout"
import { TestResultStatus, TestType } from "../enums"
import { formatSeconds, formatTimeV2, toLetter } from "../utils"
import StudentTestResult from "./StudentTestResult"
import styles from "./TestResult.module.css"
import TrueFalseBadge from './../components/tests/TrueFalseBadge';
import ReactMarkdown from "react-markdown"
const { Buffer } = require("buffer")


let statusTimeout = null
const TestResult = ({ }) => {

  const { t } = useTranslation()
  const { width } = useWindowDimensions()
  const navigate = useNavigate()
  const { testId } = useParams()

  const [loading, setLoading] = useState(false)
  const [testResult, setTestResult] = useState(null)
  const [students, setStudents] = useState([])
  const [expanded, setExpanded] = useState({})
  const [currentStudent, setCurrentStudent] = useState(null)
  const [showStudentPage, setShowStudentPage] = useState(false)
  const questionRefs = useRef({})
  const questionHighlightStartTimeoutRef = useRef(null)
  const questionHighlightTimeoutRef = useRef(null)
  const [highlightedQuestionId, setHighlightedQuestionId] = useState(null)
  const [markAsCorrectStatus, setMarkAsCorrectStatus] = useState({
    id: null,
    status: null
  })

  const innerTests = useMemo(() => {
    if (!testResult) return []
    const { innerTests } = testResult.content
    const { user_tests } = testResult
    for (let i = 0; i < innerTests.length; i++) {


      const { id, testType } = innerTests[i]

      switch (testType) {
        case TestType.SingleChoice:
        case TestType.MultipleChoice:
          { // per ogni innerTest creo una lista degli studenti che hanno risposto
            const { answers } = innerTests[i]
            let numberOfAnswers = 0
            for (let j = 0; j < answers.length; j++) {
              const { id: answerId } = answers[j]
              const answerStudents = []
              user_tests.forEach(ut => {
                const t = ut.content.filter(i => i.id === id)[0]
                if (t) {
                  const { responses } = t
                  const userResponse = responses.find(r => r.id === answerId)
                  if (userResponse.value === true) {
                    answerStudents.push(ut.user)
                  }
                }
              });
              innerTests[i].answers[j].students = answerStudents
              numberOfAnswers += answerStudents.length
            }
            let sum = 0
            for (let j = 0; j < innerTests[i].answers.length; j++) {
              const answerStudents = innerTests[i].answers[j].students
              innerTests[i].answers[j].percentage = answerStudents.length > 0 ? Math.round(answerStudents.length / numberOfAnswers * 100) : 0
              sum += innerTests[i].answers[j].percentage
            }

            if (sum < 100) {
              for (let j = 0; j < innerTests[i].answers.length; j++) {
                if (innerTests[i].answers[j].percentage > 0) {
                  innerTests[i].answers[j].percentage += (100 - sum)
                  break
                }
              }
            }
          }
          break;
        case TestType.TextCompletion:
          {
            // per ogni parola creo una lista delle parole date dagli studenti
            const { words } = innerTests[i]
            for (let j = 0; j < words.length; j++) {
              if (words[j].hidden) {
                const usersAnswers = []
                user_tests.forEach(ut => {
                  const t = ut.content.filter(i => i.id === id)[0]
                  if (t) {
                    const { responses } = t
                    const userResponse = responses.find(r => r.index === j)
                    const isCorrect = words[j].solutions.map(s => s.toLowerCase()).includes(userResponse.value.toLowerCase())
                    usersAnswers.push({
                      user: ut.user,
                      answer: userResponse.value,
                      isCorrect
                    })
                  }
                });
                innerTests[i].words[j].usersAnswers = usersAnswers
                innerTests[i].words[j].id = `${innerTests[i].words[j].text}-${i}-${j}`
                const correct = usersAnswers.filter(ua => ua.isCorrect === true).length
                const wrong = usersAnswers.filter(ua => ua.isCorrect === false).length
                const l = usersAnswers.length
                innerTests[i].words[j].correct_percentage = l > 0 ? Math.round(correct / l * 100) : 0
                innerTests[i].words[j].wrong_percentage = l > 0 ? Math.round(wrong / l * 100) : 0
              }
            }
          }
          break;
        case TestType.TrueFalse:
          { // per ogni innerTest creo una lista degli studenti che hanno risposto
            const { answers } = innerTests[i]
            for (let j = 0; j < answers.length; j++) {
              const { id: answerId, value } = answers[j]
              const usersAnswers = []
              user_tests.forEach(ut => {
                const t = ut.content.filter(i => i.id === id)[0]
                if (t) {
                  const { responses } = t
                  const userResponse = responses.find(r => r.id === answerId)
                  usersAnswers.push({
                    user: ut.user,
                    value: userResponse.value,
                    isCorrect: userResponse.value === value
                  })
                }
              });
              innerTests[i].answers[j].usersAnswers = usersAnswers
            }
            for (let j = 0; j < innerTests[i].answers.length; j++) {
              const trues = innerTests[i].answers[j].usersAnswers.filter(s => s.value).length
              const falses = innerTests[i].answers[j].usersAnswers.filter(s => s.value === false).length
              innerTests[i].answers[j].true_percentage = trues > 0 ? Math.round(trues / (trues + falses) * 100) : 0
              innerTests[i].answers[j].false_percentage = falses > 0 ? Math.round(falses / (trues + falses) * 100) : 0
            }
          }
        default: break
      }


    }

    return innerTests

  }, [testResult, students])

  const circularData = useMemo(() => {
    if (students.length === 0 || innerTests.length === 0) {
      return []
    }
    let passed = 0
    let failed = 0
    let notCompleted = 0
    const successThreshold = testResult.success_threshold ?? 0
    for (const [i, student] of students.filter(s => !testResult?.excluded?.includes(s.id)).entries()) {
      let correct = 0
      let wrong = 0
      for (const innerTest of innerTests) {
        const { testType } = innerTest
        if (testType === TestType.SingleChoice || testType === TestType.MultipleChoice) {
          const { answers } = innerTest
          for (const answer of answers) {
            const isStudentAnswer = answer.students.map((s) => s.id).includes(student.id)
            if (isStudentAnswer) {
              if (answer.isCorrect) {
                correct += 1
              } else if (!answer.isCorrect) {
                wrong += 1
              }
            }
          }
        } else if (testType === TestType.TextCompletion) {
          const { words } = innerTest
          const unknowns = words.filter(w => w.hidden)
          for (const word of unknowns) {
            const { usersAnswers } = word
            const userAnswer = usersAnswers.find(ua => ua.user.id === student.id)
            if (userAnswer && userAnswer.isCorrect) {
              correct += 1
            } else if (userAnswer && userAnswer.isCorrect === false) {
              wrong += 1
            }
          }
        } else if (testType === TestType.TrueFalse) {
          const { answers } = innerTest
          for (const answer of answers) {
            const { usersAnswers } = answer
            const userAnswer = usersAnswers.find(ua => ua.user.id === student.id)
            if (userAnswer && userAnswer.isCorrect) {
              correct += 1
            } else if (userAnswer && userAnswer.isCorrect === false) {
              wrong += 1
            }
          }
        }
      }
      student.clickable = false
      student.correct = correct
      student.wrong = wrong
      student.notAnswered = innerTests.length - (correct + wrong)
      student.started_at = testResult.user_tests.find(ut => ut.user.id === student.id)?.started_at || null
      student.submitted_at = testResult.user_tests.find(ut => ut.user.id === student.id)?.updated_at || null
      if (student.started_at && student.submitted_at) {
        student.duration = Math.round((new Date(student.submitted_at).getTime() - new Date(student.started_at).getTime()) / 1000) // in minutes
      }

      if ((correct + wrong) === 0) {
        notCompleted += 1
        student.resultStatus = TestResultStatus.NotCompleted
        student.statusClass = `${styles.studentStatus} ${styles.notCompleted}`
        student.statusLabel = t("tests.status.incomplete")
      } else if (correct > 0 && correct >= successThreshold) {
        passed += 1
        student.resultStatus = TestResultStatus.Passed
        student.statusClass = `${styles.studentStatus} ${styles.passed}`
        student.statusLabel = t("tests.status.passed")
        student.clickable = true
      } else {
        failed += 1
        student.resultStatus = TestResultStatus.Failed
        student.statusClass = `${styles.studentStatus} ${styles.failed}`
        student.statusLabel = t("tests.status.failed")
        student.clickable = true
      }
    }


    const sum = passed + failed + notCompleted

    const pp = Math.round(((passed) / sum * 100) * 100) / 100
    const pf = Math.round(((failed) / sum * 100) * 100) / 100
    const pnd = Math.round(((notCompleted) / sum * 100) * 100) / 100

    return [
      {
        id: "passed",
        value: pp,
        rawValue: passed,
        color: 'var(--sf-light-green)',
        label: t("tests.passed")
      },
      {
        id: "failed",
        value: pf,
        rawValue: failed,
        color: 'var(--sf-red)',
        label: t("tests.failed")
      },
      {
        id: "notCompleted",
        value: pnd,
        rawValue: notCompleted,
        color: '#B5B8BE',
        label: t("tests.notCompleted")
      },
    ]
  }, [students, innerTests, testResult])

  const errorAnalysis = useMemo(() => {
    if (!testResult || innerTests.length === 0) {
      return { questions: [], tags: [] }
    }

    const userTests = testResult.user_tests ?? []
    const questionStats = []
    const tagStatsMap = new Map()

    const getQuestionTitle = (innerTest, index) => {
      const text = (innerTest.text || "").trim()
      return text.length > 0 ? text : `Domanda ${index + 1}`
    }

    for (let i = 0; i < innerTests.length; i++) {
      const innerTest = innerTests[i]
      let wrongCount = 0
      let totalCount = 0

      if (innerTest.testType === TestType.SingleChoice || innerTest.testType === TestType.MultipleChoice) {
        const correctIds = (innerTest.answers || [])
          .filter(a => a.isCorrect)
          .map(a => a.id)
          .sort()

        for (const ut of userTests) {
          const studentTest = (ut.content || []).find(item => item.id === innerTest.id)
          if (!studentTest) continue

          const selectedIds = (studentTest.responses || [])
            .filter(r => r.value === true)
            .map(r => r.id)
            .sort()

          totalCount += 1
          const sameLength = selectedIds.length === correctIds.length
          const allMatch = sameLength && selectedIds.every((id, idx) => id === correctIds[idx])
          if (!allMatch) {
            wrongCount += 1
          }
        }
      }

      if (innerTest.testType === TestType.TextCompletion) {
        const hiddenWords = (innerTest.words || []).filter(w => w.hidden)
        for (const word of hiddenWords) {
          const usersAnswers = word.usersAnswers || []
          totalCount += usersAnswers.length
          wrongCount += usersAnswers.filter(ua => ua.isCorrect === false).length
        }
      }

      if (innerTest.testType === TestType.TrueFalse) {
        for (const answer of (innerTest.answers || [])) {
          const usersAnswers = answer.usersAnswers || []
          totalCount += usersAnswers.length
          wrongCount += usersAnswers.filter(ua => ua.isCorrect === false).length
        }
      }

      if (totalCount === 0) continue

      const wrongPercentage = Math.round((wrongCount / totalCount) * 100)
      const entry = {
        id: innerTest.id,
        title: getQuestionTitle(innerTest, i),
        wrongCount,
        totalCount,
        wrongPercentage,
      }
      questionStats.push(entry)

      for (const tag of (innerTest.tags || [])) {
        const key = tag.id ?? tag.name
        if (!key) continue

        if (!tagStatsMap.has(key)) {
          tagStatsMap.set(key, {
            id: key,
            name: tag.name || "Senza nome",
            wrongCount: 0,
            totalCount: 0,
          })
        }

        const current = tagStatsMap.get(key)
        current.wrongCount += wrongCount
        current.totalCount += totalCount
      }
    }

    const worstQuestions = questionStats
      .sort((a, b) => {
        if (b.wrongPercentage !== a.wrongPercentage) return b.wrongPercentage - a.wrongPercentage
        return b.wrongCount - a.wrongCount
      })
      .slice(0, 5)

    const worstTags = Array.from(tagStatsMap.values())
      .map(tag => ({
        ...tag,
        wrongPercentage: tag.totalCount > 0 ? Math.round((tag.wrongCount / tag.totalCount) * 100) : 0,
      }))
      .sort((a, b) => {
        if (b.wrongPercentage !== a.wrongPercentage) return b.wrongPercentage - a.wrongPercentage
        return b.wrongCount - a.wrongCount
      })
      .slice(0, 5)

    return {
      questions: worstQuestions,
      tags: worstTags,
    }
  }, [innerTests, testResult])

  const meanTime = useMemo(() => {
    if (!students || students.length === 0) return null

    return Math.round(students.filter(s => s.duration > 0).reduce((acc, s) => acc + (s.duration || 0), 0) / students.filter(s => s.duration > 0).length)

  }, [students])

  useEffect(() => { window.scrollTo(0, 0) }, [showStudentPage])

  useEffect(() => {

    getTestResult()

    return () => {
      if (statusTimeout) {
        clearTimeout(statusTimeout)
        statusTimeout = null
      }
    }
  }, []);


  const getTestResult = useCallback(async () => {

    const getStudents = async (moduleId, testResult) => {
      try {
        const result = await api.get(`/teacher/modules/${moduleId}/students`);

        const students = result.map((s) => {

          return {
            ...s,
            resultStatus: TestResultStatus.NotCompleted,
            statusClass: `${styles.studentStatus} ${styles.notCompleted}`,
            statusLabel: t("tests.status.incomplete")
          }
        })

        setStudents(students)
      } catch (e) {
        console.error(e)
      }
    }

    try {
      const tr = await api.get(`/teacher/tests/${testId}`);
      setTestResult(tr)
      const { module } = tr
      getStudents(module.id, tr)
    } catch (e) {
      console.error(e)
    }
  }, [])

  const onStudentClick = useCallback((studentId) => {
    const student = students.find(s => s.id === studentId)
    setCurrentStudent(student)
    setShowStudentPage(true)
  }, [students])

  const onBack = useCallback(() => {
    setShowStudentPage(false)
    setCurrentStudent(null)
  }, [])

  const onContact = useCallback(() => {
    const base64 = Buffer.from(`${currentStudent.id}_${currentStudent.name}_${currentStudent.surname}`, "utf-8").toString("base64")
    navigate(`/messages?chat=${base64}`)
  }, [currentStudent])

  const scrollToQuestion = useCallback((questionId) => {
    const questionNode = questionRefs.current[questionId]
    if (!questionNode) return
    questionNode.scrollIntoView({ behavior: "smooth", block: "center" })

    setHighlightedQuestionId(null)
    if (questionHighlightStartTimeoutRef.current) {
      clearTimeout(questionHighlightStartTimeoutRef.current)
    }
    if (questionHighlightTimeoutRef.current) {
      clearTimeout(questionHighlightTimeoutRef.current)
    }

    // Start the flash after scroll animation has progressed.
    questionHighlightStartTimeoutRef.current = setTimeout(() => {
      setHighlightedQuestionId(questionId)
      questionHighlightTimeoutRef.current = setTimeout(() => {
        setHighlightedQuestionId(null)
        questionHighlightTimeoutRef.current = null
      }, 3000)
      questionHighlightStartTimeoutRef.current = null
    }, 450)
  }, [])

  const exportAllStudentsResponsesCsv = useCallback(() => {
    if (!testResult || innerTests.length === 0) return

    const csvEscape = (value) => {
      const str = value === null || value === undefined ? "" : String(value)
      return `"${str.replace(/"/g, '""')}"`
    }

    const getQuestionTitle = (innerTest, index) => {
      const text = (innerTest.text || "").trim()
      return text.length > 0 ? text : `Domanda ${index + 1}`
    }

    const rows = [[
      "Studente",
      "Domanda",
      "Tipo",
      "Tag",
      "Voce",
      "Risposta studente",
      "Risposta corretta",
      "Esito",
    ]]

    const excludedIds = new Set(testResult.excluded || [])
    const userTests = (testResult.user_tests || []).filter(ut => !excludedIds.has(ut.user?.id))

    for (let i = 0; i < innerTests.length; i++) {
      const innerTest = innerTests[i]
      const questionTitle = getQuestionTitle(innerTest, i)
      const tags = (innerTest.tags || []).map(tag => tag.name).join(" | ")

      for (const ut of userTests) {
        const studentName = `${ut.user?.name || ""} ${ut.user?.surname || ""}`.trim() || "Studente"
        const studentTest = (ut.content || []).find(item => item.id === innerTest.id)

        if (innerTest.testType === TestType.SingleChoice || innerTest.testType === TestType.MultipleChoice) {
          const responses = studentTest?.responses || []
          const selectedIds = responses.filter(r => r.value === true).map(r => r.id).sort()
          const correctIds = (innerTest.answers || []).filter(a => a.isCorrect).map(a => a.id).sort()

          const selectedText = (innerTest.answers || [])
            .filter(a => selectedIds.includes(a.id))
            .map(a => a.text)
            .join(" | ") || "Nessuna risposta"
          const correctText = (innerTest.answers || []).filter(a => a.isCorrect).map(a => a.text).join(" | ")

          const sameLength = selectedIds.length === correctIds.length
          const isCorrect = selectedIds.length > 0 && sameLength && selectedIds.every((id, idx) => id === correctIds[idx])
          const outcome = selectedIds.length === 0 ? "Non risposta" : (isCorrect ? "Corretta" : "Sbagliata")

          rows.push([
            studentName,
            questionTitle,
            innerTest.testType === TestType.SingleChoice ? "SingleChoice" : "MultipleChoice",
            tags,
            "Risposta",
            selectedText,
            correctText,
            outcome,
          ])
        }

        if (innerTest.testType === TestType.TrueFalse) {
          const responses = studentTest?.responses || []
          for (const answer of (innerTest.answers || [])) {
            const userResponse = responses.find(r => r.id === answer.id)
            const studentValue = userResponse?.value
            const studentText = studentValue === null || studentValue === undefined ? "Nessuna risposta" : (studentValue ? "Vero" : "Falso")
            const correctText = answer.value ? "Vero" : "Falso"
            const isCorrect = studentValue === null || studentValue === undefined ? null : studentValue === answer.value
            const outcome = isCorrect === null ? "Non risposta" : (isCorrect ? "Corretta" : "Sbagliata")

            rows.push([
              studentName,
              questionTitle,
              "TrueFalse",
              tags,
              answer.text || "Affermazione",
              studentText,
              correctText,
              outcome,
            ])
          }
        }

        if (innerTest.testType === TestType.TextCompletion) {
          const hiddenWords = (innerTest.words || []).filter(w => w.hidden)
          for (const word of hiddenWords) {
            const userAnswer = (word.usersAnswers || []).find(ua => ua.user.id === ut.user.id)
            const studentText = userAnswer?.answer || "Nessuna risposta"
            const correctText = (word.solutions || []).join(" | ")
            const outcome = !userAnswer || !userAnswer.answer ? "Non risposta" : (userAnswer.isCorrect ? "Corretta" : "Sbagliata")

            rows.push([
              studentName,
              questionTitle,
              "TextCompletion",
              tags,
              word.text || "Parola",
              studentText,
              correctText,
              outcome,
            ])
          }
        }
      }
    }

    const csvContent = rows.map(row => row.map(csvEscape).join(";")).join("\n")
    const blob = new Blob([`\uFEFF${csvContent}`], { type: "text/csv;charset=utf-8;" })
    const url = URL.createObjectURL(blob)
    const link = document.createElement("a")
    link.href = url
    link.download = `risposte_test_${testId}_tutti_studenti.csv`
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
    URL.revokeObjectURL(url)
  }, [innerTests, testId, testResult])

  const markAsCorrect = useCallback(async (innerTest, userAnswer, wordId) => {
    const { user, answer } = userAnswer
    try {
      setMarkAsCorrectStatus({
        id: `${user.id}-${answer}-${wordId}`,
        status: "loading"
      })
      const i = innerTests.findIndex(it => it.id === innerTest.id)
      if (i >= 0) {

        const j = innerTests[i].words.findIndex(w => w.id === wordId)
        if (j >= 0) {
          innerTests[i].words[j].solutions.push(answer.toLowerCase())
        }
      }

      const updatedInnerTests = JSON.parse(JSON.stringify(innerTests)) // trick to deep copy and object with all its nested object
      for (let i = 0; i < updatedInnerTests.length; i++) {
        const { testType } = updatedInnerTests[i]
        if (testType === TestType.TextCompletion) {
          for (let j = 0; j < updatedInnerTests[i].words.length; j++) {
            if (updatedInnerTests[i].words[j].hidden) {
              // remove temporary data from word
              const { text, hidden, solutions, ...tempData } = updatedInnerTests[i].words[j]
              updatedInnerTests[i].words[j] = { text, hidden, solutions }
            }
          }
        } else if (testType === TestType.SingleChoice || testType === TestType.MultipleChoice) {
          for (let j = 0; j < updatedInnerTests[i].answers.length; j++) {
            // remove temporary data from answer
            const { id, text, isCorrect, ...tempData } = updatedInnerTests[i].answers[j]
            updatedInnerTests[i].answers[j] = { id, text, isCorrect }
          }
        }
      }

      const update = {
        content: { innerTests: updatedInnerTests }
      }

      await api.put(`/teacher/tests/${testId}`, update)
      setMarkAsCorrectStatus({
        id: `${user.id}-${answer}-${wordId}`,
        status: "success"
      })
      getTestResult()
    } catch (e) {
      console.error(e)
      setMarkAsCorrectStatus({
        id: `${user.id}-${answer}-${wordId}`,
        status: "error"
      })
    }

    statusTimeout = setTimeout(() => {
      setMarkAsCorrectStatus({
        id: null,
        status: null
      })
      statusTimeout = null
    }, 3000)

  }, [testId, innerTests])

  return (
    <HeaderFooterLayout>
      <HelmetProvider>
        <Helmet>
          <title>Test</title>
        </Helmet>
      </HelmetProvider>
      {
        showStudentPage === false &&
        <div className={styles.container}>
          <div className={styles.section}>
            <div className={styles.sectionInner}>
              <div className={styles.header}>
                <div className={styles.title}>{testResult?.name}</div>
              </div>
              <div className={styles.top}>
                <Card style={{ flexGrow: 1 }}>
                  <div className={styles.recap}>
                    <div className={styles.analysisHeader}>
                      <div className={styles.cardTitle}>{t("tests.recapTitle")}</div>
                      <div className={styles.analysisActions}>
                        <Button
                          accentColor="var(--secondary)"
                          appearance="subtle"
                          onClick={exportAllStudentsResponsesCsv}
                        >
                          ESPORTA RISULTATI
                        </Button>
                      </div>
                    </div>
                    <div className={styles.circularChartColumn}>
                      <CircularProgress data={circularData.filter(d => d.id !== "notCompleted")}></CircularProgress>
                      <div className={styles.chartLabels}>
                        {circularData.map(data => {
                          return (
                            <div className={styles.chartCell} key={data.label}>
                              <div className={styles.chartRow}>
                                <div className={styles.chartColumn}>
                                  <div className={styles.chartColor} style={{ backgroundColor: data.color }}></div>
                                </div>
                                <div className={styles.chartColumn}>
                                  <div className={styles.chartDataTitle}>{data.rawValue}</div>
                                  <div className={styles.chartDataSubitle} style={{ color: data.color }}>{data.label}</div>
                                </div>
                              </div>
                            </div>
                          )
                        })}
                        <div className={styles.chartCell} key={"time"}>
                          <div className={styles.chartRow}>
                            <div className={styles.chartColumn}>
                              <div className={styles.chartColor} style={{ backgroundColor: 'var(--secondary)' }}></div>
                            </div>
                            <div className={styles.chartColumn}>
                              <div className={styles.chartDataTitle}>{formatSeconds(meanTime, true)}</div>
                              <div className={styles.chartDataSubitle} style={{ color: 'var(--secondary)' }}>Tempo Medio</div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </Card>

                <Card style={{
                  flexGrow: 1,
                  maxHeight: "400px",
                  overflowY: "scroll",
                }}>
                  <div className={styles.students}>
                    <div className={styles.cardTitle}>{t("tests.students")}</div>
                    <Each
                      of={students
                        .filter(s => !testResult?.excluded?.includes(s.id))
                        .slice()
                        .sort((a, b) => {
                          const aTs = a.submitted_at ? new Date(a.submitted_at).getTime() : 0
                          const bTs = b.submitted_at ? new Date(b.submitted_at).getTime() : 0
                          if (bTs !== aTs) return bTs - aTs
                          return `${a.surname || ""} ${a.name || ""}`.localeCompare(
                            `${b.surname || ""} ${b.name || ""}`,
                            "it",
                            { sensitivity: "base" }
                          )
                        })}
                      render={(student) => (
                        <div className={styles.student}
                          key={student.id}
                          onClick={() => {
                            if (student.clickable) {
                              onStudentClick(student.id)
                            }
                          }}
                          style={{ cursor: student.clickable ? "pointer" : "default" }}
                        >
                          <div className={styles.studentMain}>
                            <img className={styles.picture} src={student.picture ?? UserPlaceholder} alt="" />
                            <div className={styles.name}>{student.name} {student.surname}</div>
                          </div>

                          <div className={styles.studentMeta}>
                            <div className={styles.studentMetaCell}>
                              <div className={styles.studentMetaLabel}>Durata</div>
                              <div className={styles.studentMetaValue}>
                                {student.duration ? formatSeconds(student.duration || 0, true) : "-"}
                              </div>
                            </div>

                            <div className={styles.studentMetaCell}>
                              <div className={styles.studentMetaLabel}>Consegnato</div>
                              <div className={styles.studentMetaValue}>
                                {student.resultStatus !== TestResultStatus.NotCompleted ? formatTimeV2(student.submitted_at) : "-"}
                              </div>
                            </div>
                          </div>

                          <div className={student.statusClass}>
                            {(student.correct + student.wrong) > 0 &&
                              `(${student.correct}/${innerTests.length}) `
                            }
                            {student.statusLabel.toUpperCase()}
                          </div>
                        </div>
                      )}
                    />
                  </div>
                </Card>
                <div className={styles.content}>
                  <Card style={{ flexGrow: 1 }}>
                    <div className={styles.analysisContainer}>
                      <div className={styles.cardTitle}>Analisi Errori</div>
                      <div className={styles.analysisGrid}>
                        <div className={styles.analysisColumn}>
                          <div className={styles.analysisTitle}>Domande</div>
                          {
                            errorAnalysis.questions.length === 0 &&
                            <div className={styles.analysisEmpty}>Nessun dato disponibile</div>
                          }
                          <Each
                            of={errorAnalysis.questions}
                            render={(item, index) => (
                              <div
                                className={`${styles.analysisRow} ${styles.analysisRowClickable}`}
                                key={`q-${item.id}`}
                                onClick={() => scrollToQuestion(item.id)}
                              >
                                <div className={styles.analysisRank}>{index + 1}.</div>
                                <div className={styles.analysisText}>{item.title}</div>
                                <div className={styles.analysisValue}>{item.wrongPercentage}%</div>
                              </div>
                            )}
                          />
                        </div>

                        <div className={styles.analysisColumn}>
                          <div className={styles.analysisTitle}>Argomenti</div>
                          {
                            errorAnalysis.tags.length === 0 &&
                            <div className={styles.analysisEmpty}>Nessun tag disponibile</div>
                          }
                          <Each
                            of={errorAnalysis.tags}
                            render={(item, index) => (
                              <div className={styles.analysisRow} key={`t-${item.id}`}>
                                <div className={styles.analysisRank}>{index + 1}.</div>
                                <div className={styles.analysisText}>{item.name}</div>
                                <div className={styles.analysisValue}>{item.wrongPercentage}%</div>
                              </div>
                            )}
                          />
                        </div>
                      </div>
                    </div>
                  </Card>

                  <Each
                    of={innerTests}
                    render={(innerTest, innerTestIndex) => (
                      <div
                        key={innerTest.id}
                        ref={(node) => {
                          if (node) {
                            questionRefs.current[innerTest.id] = node
                          }
                        }}
                      >
                        <Card style={{ flexGrow: 1, padding: innerTest.testType === TestType.TrueFalse ? "2rem 0" : 0 }}>
                          <div className={highlightedQuestionId === innerTest.id ? `${styles.innerTest} ${styles.questionHighlighted}` : styles.innerTest}>
                          {
                            innerTest.text &&
                            <div className={styles.cardTitle} style={{
                              padding: "0 1rem",
                              paddingTop: "1rem",
                            }}>{innerTest.text}
                            </div>
                          }
                          {
                            Array.isArray(innerTest.tags) && innerTest.tags.length > 0 &&
                            <div className={styles.questionTags}>
                              <Each
                                of={innerTest.tags}
                                render={(tag) => (
                                  <Tag key={`${innerTest.id}-${tag.id ?? tag.name}`} tag={tag} />
                                )}
                              />
                            </div>
                          }
                          {
                            innerTest.testType === TestType.TextCompletion &&
                            <div className={styles.words}>
                              <Each
                                of={innerTest.words}
                                render={(word) => (
                                  <div className={word.hidden ? `${styles.word} ${styles.hidden}` : styles.word}>{word.text}</div>
                                )}
                              />
                            </div>
                          }
                          {
                            (innerTest.testType === TestType.SingleChoice || innerTest.testType === TestType.MultipleChoice) &&
                            <Each
                              of={innerTest.answers}
                              render={(answer, index) => (
                                <div className={styles.answerContainer}>
                                  <div
                                    className={answer.isCorrect ? `${styles.mcAnswerCard} ${styles.mcAnswerCardCorrect}` : styles.mcAnswerCard}
                                    onClick={() => {
                                      if (answer.percentage === 0) {
                                        return
                                      }

                                      if (expanded === answer.id) {
                                        setExpanded(null)
                                      } else {
                                        setExpanded(answer.id)
                                      }
                                    }}
                                  >
                                    <div className={styles.mcAnswerHeader}>
                                      <div className={styles.left}>
                                        <div className={answer.isCorrect ? styles.correctLabel : styles.wrongLabel}>{toLetter(index + 1).toUpperCase()}</div>
                                        <ReactMarkdown children={answer.text} />
                                      </div>
                                      <div className={answer.percentage === 0 ? `${styles.chevron} ${styles.disabled}` :
                                        expanded === answer.id ? `${styles.chevron} ${styles.open}` : styles.chevron}
                                      >
                                        <ChevronIcon className={expanded === answer.id ? styles.open : styles.closed} />
                                      </div>
                                    </div>
                                    <div className={styles.mcAnswerStats}>
                                      <div className={answer.isCorrect ? `${styles.percentage} ${styles.correct}` : styles.percentage}>{answer.percentage}%</div>
                                      <div className={styles.barContainer}>
                                        <div className={answer.isCorrect ? `${styles.bar} ${styles.correct}` : styles.bar}
                                          style={{
                                            width: answer.percentage === 0 ? "2%" : `${answer.percentage}%`
                                          }}
                                        />
                                      </div>
                                    </div>
                                    <SimpleCollapsable
                                      expanded={expanded === answer.id}
                                      expadendHeight={`auto`}
                                      style={{
                                        backgroundColor: "transparent"
                                      }}
                                    >
                                      <div className={styles.answerStudents}>
                                        <Each
                                          of={answer.students
                                            .slice()
                                            .sort((a, b) => `${a.surname || ""} ${a.name || ""}`.localeCompare(`${b.surname || ""} ${b.name || ""}`, "it", { sensitivity: "base" }))}
                                          render={(student) => (
                                            <div
                                              className={`${styles.student} ${styles.answerStudentCard}`}
                                              onClick={(e) => {
                                                e.stopPropagation()
                                                onStudentClick(student.id)
                                              }}
                                            >
                                              <div className={styles.answerStudentIdentity}>
                                                <span className={styles.answerStudentSurname}>{student.surname}</span>
                                                <span className={styles.answerStudentName}>{student.name}</span>
                                              </div>
                                            </div>
                                          )}
                                        />
                                      </div>
                                    </SimpleCollapsable>
                                  </div>
                                </div>
                              )}
                            />
                          }
                          {
                            innerTest.testType === TestType.TextCompletion &&
                            <Each
                              of={innerTest.words.filter(w => w.hidden === true)}
                              render={(word) => (
                                <div className={styles.answerContainer}>
                                  <div className={styles.answerWrapper}>
                                    <div className={styles.answer} style={{ fontWeight: 800, color: "var(--sf-light-green)", width: "50%", flexGrow: 0 }}>
                                      {word.text}
                                    </div>
                                    <div className={styles.wordStats}>
                                      <div className={styles.wordStat}>
                                        <div className={`${styles.percentage} ${styles.correct}`}>{word.correct_percentage}%</div>
                                        <div className={styles.barContainer}>
                                          <div className={`${styles.bar} ${styles.correct}`}
                                            style={{
                                              width: word.correct_percentage === 0 ? "2%" : `${word.correct_percentage}%`,
                                              minHeight: "16px"
                                            }}
                                          />
                                        </div>
                                      </div>
                                      <div className={styles.wordStat}>
                                        <div className={styles.percentage}>{word.wrong_percentage}%</div>
                                        <div className={styles.barContainer}>
                                          <div className={styles.bar}
                                            style={{
                                              width: word.wrong_percentage === 0 ? "2%" : `${word.wrong_percentage}%`,
                                              minHeight: "16px"
                                            }}
                                          />
                                        </div>
                                      </div>
                                    </div>
                                    <div className={expanded === word.id ? `${styles.chevron} ${styles.open}` : styles.chevron}
                                      onClick={() => {
                                        if (expanded === word.id) {
                                          setExpanded(null)
                                        } else {
                                          setExpanded(word.id)
                                        }
                                      }}
                                    >
                                      <ChevronIcon className={expanded === word.id ? styles.open : styles.closed} />
                                    </div>
                                  </div>
                                  <SimpleCollapsable
                                    expanded={expanded === word.id}
                                    expadendHeight={`auto`}
                                    style={{
                                      backgroundColor: "transparent"
                                    }}
                                  >
                                    <div className={styles.answerStudents}>
                                      <Each
                                        of={word.usersAnswers
                                          .slice()
                                          .sort((a, b) => `${a.user?.surname || ""} ${a.user?.name || ""}`.localeCompare(`${b.user?.surname || ""} ${b.user?.name || ""}`, "it", { sensitivity: "base" }))}
                                        render={(userAnswer, index) => (
                                          <div className={`${styles.student} ${styles.answerStudentCard}`} onClick={() => onStudentClick(userAnswer.user.id)}>
                                            <div className={`${styles.studentWordContainer} ${styles.answerStudentWordContainer}`}>
                                              <div className={styles.answerStudentIdentity}>
                                                <span className={styles.answerStudentSurname}>{userAnswer.user.surname}</span>
                                                <span className={styles.answerStudentName}>{userAnswer.user.name}</span>
                                              </div>
                                              <div className={userAnswer.isCorrect ? styles.studentWord : `${styles.studentWord} ${styles.wrong}`}>{userAnswer.answer || "Nessuna risposta"}</div>
                                            </div>
                                            {
                                              !userAnswer.isCorrect && userAnswer.answer &&
                                              <Button
                                                inverse
                                                accentColor={"var(--tertiary)"}
                                                style={{ padding: ".5rem .8rem", borderRadius: "10px" }}
                                                onClick={(e) => {
                                                  e.stopPropagation()
                                                  markAsCorrect(innerTest, userAnswer, word.id)
                                                }}
                                              >
                                                {t("tests.testType.textCompletion.markAsCorrect").toUpperCase()}
                                                {markAsCorrectStatus.id === `${userAnswer.user.id}-${userAnswer.answer}-${word.id}` &&
                                                  <>
                                                    {
                                                      markAsCorrectStatus.status === "loading" &&
                                                      <span className="loader" style={{ "--accent-color": "var(--tertiary)" }} />
                                                    }
                                                    {
                                                      markAsCorrectStatus.status === "success" &&
                                                      <CheckIcon className={styles.successIcon} style={{
                                                        color: "var(--sf-light-green)"
                                                      }} />
                                                    }
                                                    {
                                                      markAsCorrectStatus.status === "error" &&
                                                      <ErrorIcon className={styles.errorIcon} style={{ color: "var(--sf-red)" }} />
                                                    }
                                                  </>
                                                }
                                              </Button>
                                            }
                                          </div>
                                        )}
                                      />
                                    </div>
                                  </SimpleCollapsable>
                                </div>
                              )}
                            />
                          }

                          {
                            innerTest.testType === TestType.TrueFalse &&
                            <Each
                              of={innerTest.answers}
                              render={(answer, index) => (
                                <div className={styles.answerContainer}>
                                  <div className={styles.answerWrapper}>
                                    <div className={styles.answer} style={{ fontWeight: 600, padding: "4px 1rem", width: "50%", flexGrow: 0 }}>
                                      <ReactMarkdown children={answer.text} />
                                      <div className={styles.badges}>
                                        <TrueFalseBadge type={true} onChange={null} value={answer.value} style={{ padding: "2px .5rem", fontSize: ".875rem" }} />
                                        <TrueFalseBadge type={false} onChange={null} value={answer.value} style={{ padding: "2px .5rem", fontSize: ".875rem" }} />
                                      </div>
                                    </div>
                                    <div className={styles.wordStats}>
                                      <div className={styles.wordStat}>
                                        <div className={`${styles.percentage} ${answer.true_percentage > 0 ? styles.correct : ""}`}>{answer.true_percentage}%</div>
                                        <div className={styles.barContainer}>
                                          <div className={`${styles.bar} ${answer.true_percentage > 0 ? styles.correct : ""}`}
                                            style={{
                                              width: answer.true_percentage === 0 ? "2%" : `${answer.true_percentage}%`,
                                              minHeight: "16px"
                                            }}
                                          />
                                        </div>
                                      </div>
                                      <div className={styles.wordStat}>
                                        <div className={`${styles.percentage} ${answer.false_percentage > 0 ? styles.wrong : ""}`}>{answer.false_percentage}%</div>
                                        <div className={styles.barContainer}>
                                          <div className={`${styles.bar} ${answer.false_percentage > 0 ? styles.wrong : ""}`}
                                            style={{
                                              width: answer.false_percentage === 0 ? "2%" : `${answer.false_percentage}%`,
                                              minHeight: "16px"
                                            }}
                                          />
                                        </div>
                                      </div>
                                    </div>
                                    <div className={expanded === answer.id ? `${styles.chevron} ${styles.open}` : styles.chevron}
                                      onClick={() => {
                                        if (expanded === answer.id) {
                                          setExpanded(null)
                                        } else {
                                          setExpanded(answer.id)
                                        }
                                      }}
                                    >
                                      <ChevronIcon className={expanded === answer.id ? styles.open : styles.closed} />
                                    </div>
                                  </div>
                                  <SimpleCollapsable
                                    expanded={expanded === answer.id}
                                    expadendHeight={`auto`}
                                    style={{
                                      backgroundColor: "transparent"
                                    }}
                                  >
                                    <div className={styles.answerStudents}>
                                      <Each
                                        of={answer.usersAnswers
                                          .slice()
                                          .sort((a, b) => `${a.user?.surname || ""} ${a.user?.name || ""}`.localeCompare(`${b.user?.surname || ""} ${b.user?.name || ""}`, "it", { sensitivity: "base" }))}
                                        render={(userAnswer) => (
                                          <div className={`${styles.student} ${styles.answerStudentCard}`} onClick={() => onStudentClick(userAnswer.user.id)}>
                                            <div className={styles.answerStudentIdentity}>
                                              <span className={styles.answerStudentSurname}>{userAnswer.user.surname}</span>
                                              <span className={styles.answerStudentName}>{userAnswer.user.name}</span>
                                            </div>
                                            {
                                              userAnswer.value !== null &&
                                              <TrueFalseBadge type={userAnswer.value} value={answer.value} onChange={null} />
                                            }
                                            {
                                              userAnswer.value === null &&
                                              <div className={`${styles.studentWord} ${styles.wrong}`}>Nessuna risposta</div>
                                            }
                                          </div>
                                        )}
                                      />
                                    </div>
                                  </SimpleCollapsable>
                                </div>
                              )}

                            />
                          }
                          </div>
                        </Card>
                      </div>

                    )}
                  />
                </div>

              </div>
            </div>
          </div>
        </div >
      }

      {
        showStudentPage &&
        <div className={styles.container}>
          <div className={styles.section}>
            <div className={styles.sectionInner}>
              <StudentTestResult
                student={currentStudent}
                successThreshold={testResult.success_threshold}
                test={innerTests}
                onBack={onBack}
                onContact={onContact}
              />
            </div>
          </div>
        </div>
      }
    </HeaderFooterLayout >
  )
}

export default TestResult
